const KEY = "hp_player_prefs";

const validRate = (value: unknown): value is number => typeof value === "number" && value >= 0.25 && value <= 4;

/** Device settings of the player; not tied to the listener or the account */
export interface PlayerPrefs {
    /** Speed for shows without one of their own (what the single device-wide speed used to be) */
    rate: number;
    /** Speed the listener chose per podcast, by podcast id */
    rates: Record<string, number>;
    volume: number;
    muted: boolean;
    /** Play on through the list when an episode ends; on when not chosen */
    autoAdvance?: boolean;
}

export const readPlayerPrefs = (): Partial<PlayerPrefs> => {
    try {
        const parsed: unknown = JSON.parse(localStorage.getItem(KEY) ?? "null");
        if (!parsed || typeof parsed !== "object") return {};

        const { rate, rates, volume, muted, autoAdvance } = parsed as Record<string, unknown>;
        const podcastRates =
            rates && typeof rates === "object"
                ? Object.fromEntries(Object.entries(rates).filter(([, value]) => validRate(value)))
                : {};
        return {
            ...(validRate(rate) ? { rate } : {}),
            ...(Object.keys(podcastRates).length > 0 ? { rates: podcastRates as Record<string, number> } : {}),
            ...(typeof volume === "number" && volume >= 0 && volume <= 1 ? { volume } : {}),
            ...(typeof muted === "boolean" ? { muted } : {}),
            ...(typeof autoAdvance === "boolean" ? { autoAdvance } : {}),
        };
    } catch {
        return {};
    }
};

/** The speed to play a podcast at: the one chosen for that show, else the default, else normal speed */
export const rateForPodcast = (prefs: Partial<PlayerPrefs>, podcastId: number | string): number =>
    prefs.rates?.[String(podcastId)] ?? prefs.rate ?? 1;

/**
 * Changes some of the device settings and keeps the rest. A key given as undefined is taken away.
 * Writes the whole object, so two places changing different keys do not undo each other.
 */
export const updatePlayerPrefs = (change: Partial<PlayerPrefs>) => {
    try {
        const current = readPlayerPrefs();
        const next = { ...current, ...change };
        for (const key of Object.keys(next) as (keyof PlayerPrefs)[]) {
            if (next[key] === undefined) delete next[key];
        }
        localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
        // Blocked or full: the settings then only last until the page is closed
    }
};

export const writePlayerPrefs = (prefs: PlayerPrefs) => {
    try {
        localStorage.setItem(KEY, JSON.stringify(prefs));
    } catch {
        // Blocked or full: the settings then only last until the page is closed
    }
};

const CLOSED_KEY = "hp_player_closed_at";

/** When the listener last closed the player, so that it stays closed after a reload (milliseconds; 0 if never) */
export const readPlayerClosedAt = (): number => {
    try {
        const value = Number(localStorage.getItem(CLOSED_KEY));
        return Number.isFinite(value) ? value : 0;
    } catch {
        return 0;
    }
};

export const writePlayerClosedAt = (time: number) => {
    try {
        localStorage.setItem(CLOSED_KEY, String(time));
    } catch {
        // Blocked or full: the player then comes back after a reload
    }
};
